/**
 * LibraryFilterSheet - 図書館フィルタ選択 BottomSheet
 *
 * mapping (library_id → 館名) から一覧を作り、キーワードで絞り込み＆チェックボックスで選択する。
 * 選択状態は useLibraryFilterStore 経由でサーバに自動保存される。
 */

import { useMemo, useState } from 'react'

import { BottomSheet } from '@/components/ui/bottom-sheet'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { useLibraryFilterStore } from '@/store/useLibraryFilterStore'
import { useLibrarySearchStore } from '@/store/useLibrarySearchStore'

import { buildLibraryEntries, filterLibrariesByName } from '../lib/library-mapping'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export const LibraryFilterSheet = ({ open, onOpenChange }: Props) => {
  const mapping = useLibrarySearchStore((s) => s.mapping)
  const selectedIds = useLibraryFilterStore((s) => s.selectedLibraryIds)
  const toggle = useLibraryFilterStore((s) => s.toggle)
  const clear = useLibraryFilterStore((s) => s.clear)
  const setMany = useLibraryFilterStore((s) => s.setMany)

  const [keyword, setKeyword] = useState('')

  const allEntries = useMemo(() => buildLibraryEntries(mapping), [mapping])
  const filteredEntries = useMemo(() => filterLibrariesByName(allEntries, keyword), [allEntries, keyword])

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds])

  const selectAllVisible = () => {
    const merged = new Set(selectedIds)
    for (const e of filteredEntries) merged.add(e.id)
    void setMany(Array.from(merged))
  }

  const deselectAllVisible = () => {
    const visibleSet = new Set(filteredEntries.map((e) => e.id))
    void setMany(selectedIds.filter((id) => !visibleSet.has(id)))
  }

  return (
    <BottomSheet title="図書館で絞り込み" open={open} onOpenChange={onOpenChange} snapPoints={[0.92]}>
      <div className="flex h-full flex-col gap-3 px-4 pb-4">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{selectedIds.length > 0 ? `${selectedIds.length}館を選択中` : 'フィルタなし (全館表示)'}</span>
          {selectedIds.length > 0 && (
            <button type="button" onClick={() => void clear()} className="cursor-pointer text-primary hover:underline">
              すべてクリア
            </button>
          )}
        </div>

        <Input value={keyword} onChange={(e) => setKeyword(e.target.value)} placeholder="館名で検索" />

        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={selectAllVisible}>
            表示中を全選択
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={deselectAllVisible}>
            表示中を全解除
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto rounded border border-border/40">
          {filteredEntries.length === 0 ? (
            <div className="p-4 text-center text-sm text-muted-foreground">該当する図書館がありません</div>
          ) : (
            <ul className="divide-y divide-border/30">
              {filteredEntries.map((entry) => {
                const checked = selectedSet.has(entry.id)
                const inputId = `library-filter-${entry.id}`
                return (
                  <li key={entry.id}>
                    <div className="flex items-center gap-3 px-3 py-2 hover:bg-accent/30">
                      <Checkbox id={inputId} checked={checked} onCheckedChange={() => void toggle(entry.id)} />
                      <label htmlFor={inputId} className="flex-1 cursor-pointer text-sm">
                        {entry.name}
                      </label>
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </div>
    </BottomSheet>
  )
}
